/**
 * Adding to a day rather than to a cell (8 Oct 2026).
 *
 * The desktop grid only took additions through its cells, and the first cell
 * a cook meets is breakfast. One cook put both of her non-recipe suppers on
 * breakfast — « Dumplings et légumes » on Thursday, « Sue's » on Friday —
 * cleared them and started again, then asked for an « Ajouter » per day. The
 * day's button opens the drawer on the meal a cook is most likely adding, and
 * the drawer can switch meals before anything is written.
 *
 * Pure `.ts` so Jest can import it.
 */

import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import { addDays } from '../../../utils/localDate';
import { isSlotOpen, type GenerationClock } from '../../meal-config/useGenerateWeek';
import { slotKey } from './constants';

/** A meal of the day: still free, already holding something, or over. */
export type MealSlotState = 'free' | 'full' | 'past';

/** Breakfast, lunch and dinner of day `di`, in that order. */
export function mealStatesForDay(
  slotMeals: Map<number, PlannedMeal[]>,
  weekStart: string,
  di: number,
  clock: GenerationClock,
): MealSlotState[] {
  const date = addDays(weekStart, di);
  return [0, 1, 2].map((mi) => {
    if (!isSlotOpen(date, mi, clock)) return 'past';
    return (slotMeals.get(slotKey(mi, di))?.length ?? 0) > 0 ? 'full' : 'free';
  });
}

/**
 * The meal the day's « Ajouter » opens on: supper when it is free — supper is
 * what people plan, and at 9:27 she wanted supper, not breakfast — otherwise
 * the first free meal. Null when the day has nothing left to fill.
 */
export function defaultMealForDay(states: MealSlotState[]): number | null {
  if (states[2] === 'free') return 2;
  const first = states.indexOf('free');
  return first === -1 ? null : first;
}
